import React, { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useTesterMyProgress } from '../../../hooks/analytics/useTesterReports.ts';
import { StatCard, WidgetCard, formatPercent } from '../controller/shared.tsx';
import { CHART_TOOLTIP_STYLE, SEMANTIC } from '../theme.ts';

const STATUS_COLORS: Record<string, string> = {
  completed: SEMANTIC.good.chart,
  in_progress: SEMANTIC.info.chart,
  draft: SEMANTIC.neutral.chart,
};

/**
 * Report #32 — My Test Progress. Donut of the caller's current test runs by
 * workflow_status, plus an overall "% complete" figure (completed / total)
 * and summed passed/failed attribute counts across all runs.
 */
export const MyProgressDonut: React.FC<{ assigneeUserId?: number | null }> = ({ assigneeUserId }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useTesterMyProgress(assigneeUserId);
  const rows = data ?? [];

  const { pieData, total, completed, passed, failed, pctComplete } = useMemo(() => {
    const total = rows.reduce((sum, r) => sum + (r.controls ?? 0), 0);
    const completed = rows
      .filter((r) => (r.workflow_status ?? '').toLowerCase() === 'completed')
      .reduce((sum, r) => sum + (r.controls ?? 0), 0);
    const passed = rows.reduce((sum, r) => sum + (r.passed ?? 0), 0);
    const failed = rows.reduce((sum, r) => sum + (r.failed ?? 0), 0);
    const pctComplete = total > 0 ? (completed / total) * 100 : null;
    const pieData = rows.map((r) => ({
      name: r.workflow_status,
      value: r.controls ?? 0,
    }));
    return { pieData, total, completed, passed, failed, pctComplete };
  }, [rows]);

  return (
    <WidgetCard
      title="My Test Progress"
      description="Your current test runs by workflow status"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load your test progress.'}
      isEmpty={!isLoading && !isError && total === 0}
      emptyMessage="No test runs assigned to you yet."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="relative h-40 w-40 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={70} paddingAngle={3} stroke="none">
                {pieData.map((entry) => (
                  <Cell
                    key={entry.name}
                    fill={STATUS_COLORS[(entry.name ?? '').toLowerCase()] ?? SEMANTIC.neutral.chart}
                    stroke="#fff"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [value, name?.replace(/_/g, ' ')]}
                contentStyle={CHART_TOOLTIP_STYLE}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-bold text-gray-900">{formatPercent(pctComplete)}</span>
            <span className="text-[10px] text-gray-500">complete</span>
          </div>
        </div>
        <div className="analytics-kpi-grid flex-1 w-full gap-3">
          <StatCard title="Total Controls" value={total} color="bg-gray-100 text-gray-600" />
          <StatCard title="Completed" value={completed} color="bg-green-100 text-green-600" />
          <StatCard title="Attributes Passed" value={passed} color="bg-green-100 text-green-600" />
          <StatCard title="Attributes Failed" value={failed} color="bg-red-100 text-red-600" />
        </div>
      </div>
    </WidgetCard>
  );
};

export default MyProgressDonut;
